/**
 * The isometric world map (D148). Ported from `misc/design_handoff_hedgerow`, with the one
 * change D149 asks for: a parcel is a set of tiles, so every outline, every texture pass
 * and — the part that bites — every hit test works on the union of those tiles rather than
 * on a bounding box. An L-shaped field must not steal a tap from the field in its notch.
 *
 * The scene is built as an SVG string and handed to one `<g>`, which is how the handoff
 * paints several hundred shapes a frame without Preact diffing any of them. Everything the
 * string is built from is pure: the parcels come from the idle map (`idle/map.ts`), the
 * colours from `palette`, and the only reach outside the arguments is `resolveMapTokens`,
 * which reads the theme's custom properties off this element.
 *
 * D150 adds what the flat map already did and the handoff left out: the three overlays,
 * the placement ghost, the guide ring and the weather. They are painted in two passes, in
 * `iso-paint.ts`. The washes join the land in the first SVG, under the hour's cast and the
 * weather, so they are lit with the board. The marks — the reach line, the power flag, the
 * ghost, the selection ring and the guide ring — go in a second SVG over the cast, so none
 * of them can be swallowed by a storm at dusk. The screen's own layout is still not here:
 * `children` renders over both for that.
 *
 * D168 adds `still`. Anything drawn on a map answers to this language, and two of the
 * places that draw one — a neighbour's world and the seed preview — are pictures rather
 * than the screen you play on. They want the painters and none of the plumbing, so `still`
 * drops the handlers, the zoom cluster and the grab cursor and lets the frame take the
 * diamond's own aspect. The scene above it is the same scene, painted the same way.
 */
import type { JSX } from 'preact';
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import type { Weather } from '@hedgerow/sim';
import { OVER_MAP } from '../game/map-room.ts';
import {
  SLAB,
  atCycle,
  frontCorner,
  inUnion,
  isPlan,
  northOnScreen,
  parseTileKey,
  polyPath,
  project,
  rectPts,
  resolveMapTokens,
  shade,
  stepTurn,
  unionBounds,
  unproject,
  withPlan,
  withTurn,
  type Turn,
  worldOutline,
  type Bounds,
  type WorldBounds,
  type BuildingUse,
  type IsoParcel,
  type Palette,
  type MapTokens,
  type Point,
  READY_LIFT,
  READY_TAP_R,
  type SeasonKey,
  type TileKey,
  type TimeKey,
  type Heart,
  worldAge,
  snowed,
  COMPASS_AT,
} from './iso.ts';
import {
  JOB_RIG,
  runSeconds,
  paintGhost,
  paintGuide,
  paintOverlayMarks,
  paintLanes,
  paintOverlayWash,
  paintPaving,
  paintFiller,
  type FillerProp,
  paintSelection,
  sortStanding,
  SIGN_ZOOM,
  type BuildingKind,
  type BuildingStyle,
  type IsoOverlay,
  type MachineAt,
  type Prop,
} from './iso-paint.ts';
import { SMOKE } from '../idle/appearance.ts';
import { cap, t as term } from '../idle/terms.ts';
import { prefersReducedMotion } from '../juice/motion.ts';
import {
  COMPASS_NORTH,
  MAP_PLAN_BUTTON,
  mapPlanLabel,
  MAP_TURN_BUTTON,
  mapTurnLabel,
  compassLabel,
} from '../game/copy.ts';
import { mapKitFor, type MapKit } from './iso-kit.ts';

/** The pan and the zoom, in screen pixels and a scale. */
export interface View {
  x: number;
  y: number;
  k: number;
}

/** The handoff's clamp. A map outside this either loses the world or loses the detail. */
export const ZOOM_MIN = 0.45,
  ZOOM_MAX = 2.6;

/** Wheel and button steps, the handoff's own. */
export const WHEEL_IN = 1.12,
  WHEEL_OUT = 0.89,
  BUTTON_IN = 1.25,
  BUTTON_OUT = 0.8;

/** Cumulative movement under this is a tap rather than a drag. */
export const TAP_SLOP = 6;

/** Fit slack: 24px across, and the slab plus headroom for the tallest thing standing on it. */
export const FIT_SLACK_X = 24,
  FIT_SLACK_Y = SLAB + 30;

export const clampZoom = (k: number): number => Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, k));

/** Zoom about a point in the frame, holding that point still. */
export function zoomAbout(v: View, factor: number, px: number, py: number): View {
  const k = clampZoom(v.k * factor);
  return { k, x: px - (px - v.x) * (k / v.k), y: py - (py - v.y) * (k / v.k) };
}

/** What a pinch remembers from the moment the second finger lands. */
export interface PinchStart extends View {
  /** The distance between the fingers then. */
  dist: number;
  /** The midpoint between them, relative to the frame. */
  mx: number;
  my: number;
}

/**
 * The view part way through a pinch. The midpoint between the fingers is held still while
 * the scale changes; without that the map walks across the screen as the fingers move.
 */
export function pinchView(start: PinchStart, dist: number): View {
  if (!start.dist) return { x: start.x, y: start.y, k: start.k };
  const k = clampZoom(start.k * (dist / start.dist));
  return {
    k,
    x: start.mx - (start.mx - start.x) * (k / start.k),
    y: start.my - (start.my - start.y) * (k / start.k),
  };
}

/**
 * The view that fits a plot to a frame, or null if there is nothing to fit it to.
 *
 * The early return is the whole point of this function being separate. A host that sizes
 * the map with a percentage measures 0 by 0 on mount; a zero reaching the scale makes k
 * zero, the map paints as nothing and there is no way back short of a remount.
 */
/**
 * The width-over-height a still map wants for a plot (D168). A 2:1 diamond is always twice
 * as wide as it is tall, so a still that is given a width and left to find its own height
 * takes this ratio and the fit then has no slack in either direction. Without it a caller
 * would have to guess a height, and guessing high puts a band of sky under every picture.
 */
export function stillAspect(plot: Bounds | null | undefined): number {
  if (!plot || !plot.w || !plot.h) return 2;
  const corners = rectPts(plot.x, plot.y, plot.w, plot.h);
  const xs = corners.map((c) => c[0]),
    ys = corners.map((c) => c[1]);
  const bw = Math.max(...xs) - Math.min(...xs) + FIT_SLACK_X;
  const bh = Math.max(...ys) - Math.min(...ys) + FIT_SLACK_Y;
  return bw / bh;
}

/** D259: something standing on the plot, by its footprint and how tall it is drawn. */
export interface Standing {
  x: number;
  y: number;
  w: number;
  h: number;
  /** Screen units above its ground at rest: `standingHeight` for its kind. */
  z: number;
}

/** D259: the padding a still keeps between the world as drawn and the edge of its frame. */
export const STILL_PAD = 12;

/**
 * D259: the box the world is actually drawn in: the slab with its skirt, and the top of
 * everything standing on it, so a silo or a barn at the back is never cut off.
 */
export function drawnBounds(
  plot: Bounds,
  standing: Standing[],
): { minX: number; minY: number; maxX: number; maxY: number } {
  const plan = isPlan();
  const pts: Point[] = rectPts(plot.x, plot.y, plot.w, plot.h);
  const xs = pts.map((c) => c[0]),
    ys = pts.map((c) => c[1]);
  const maxY = Math.max(...ys) + (plan ? 0 : SLAB);
  let minX = Math.min(...xs),
    maxX = Math.max(...xs),
    minY = Math.min(...ys);
  for (const s of standing) {
    const lift = plan ? 0 : s.z;
    for (const c of rectPts(s.x, s.y, s.w, s.h)) {
      minX = Math.min(minX, c[0]);
      maxX = Math.max(maxX, c[0]);
      minY = Math.min(minY, c[1] - lift);
    }
  }
  return { minX, minY, maxX, maxY };
}

/** D259: the view that frames the world as drawn, with `STILL_PAD` all round. */
export function fitDrawn(
  plot: Bounds | null | undefined,
  standing: Standing[],
  w: number,
  h: number,
): View | null {
  if (!plot || !plot.w || !plot.h || !w || !h) return null;
  const b = drawnBounds(plot, standing);
  const bw = b.maxX - b.minX,
    bh = b.maxY - b.minY;
  if (!bw || !bh) return null;
  const k = clampZoom(Math.min((w - STILL_PAD * 2) / bw, (h - STILL_PAD * 2) / bh));
  return { k, x: w / 2 - (b.minX + bw / 2) * k, y: h / 2 - (b.minY + bh / 2) * k };
}

export function fitView(plot: Bounds | null | undefined, w: number, h: number): View | null {
  if (!plot || !plot.w || !plot.h || !w || !h) return null;
  const corners = rectPts(plot.x, plot.y, plot.w, plot.h);
  const xs = corners.map((c) => c[0]),
    ys = corners.map((c) => c[1]);
  const minX = Math.min(...xs),
    minY = Math.min(...ys);
  /* D178: the headroom and the lift are the slab's thickness and the tallest roof standing
     on it. From above nothing stands and the slab has no skirt, so the farm is framed on
     its own footprint and sits in the middle of the frame rather than below it. */
  const plan = isPlan();
  const bw = Math.max(...xs) - minX;
  const bh = Math.max(...ys) - minY + (plan ? FIT_SLACK_X : FIT_SLACK_Y);
  const k = clampZoom(Math.min(w / (bw + FIT_SLACK_X), h / bh));
  const lift = plan ? 0 : 22;
  return { k, x: w / 2 - (minX + bw / 2) * k, y: h / 2 - (minY + bh / 2 - lift) * k };
}

/* ---------- seen from above (D178) ----------
   The choice between the two projections is view state on the device, like the zoom and
   the turn: nothing is sent to the server and the sim never hears of it. It is remembered
   because a player who reads their farm from above should not be put back on the
   isometric view every time they open the screen. */

/** Where the device remembers that the world was left seen from above. */
export const MAP_PLAN_KEY = 'hedgerow-map-plan';

/** Whether this device left the world seen from above. False wherever storage is shut. */
export function readPlanView(): boolean {
  try {
    return localStorage.getItem(MAP_PLAN_KEY) === 'above';
  } catch {
    /* Private windows can refuse storage. The choice simply does not outlive the session. */
    return false;
  }
}

/** Remember the choice, or forget it. */
export function writePlanView(on: boolean): void {
  try {
    if (on) localStorage.setItem(MAP_PLAN_KEY, 'above');
    else localStorage.removeItem(MAP_PLAN_KEY);
  } catch {
    /* As above: nothing to do, and nothing about the map depends on it having worked. */
  }
}

/**
 * The world point the world turns about (D176): the middle of the plot it stands on. Turning
 * about the plot's own centre is what keeps the world where it was on screen, so a quarter
 * turn does not throw away the zoom and the pan the player had set.
 */
export function turnPivot(plot: Bounds | null | undefined): Point {
  if (!plot || !plot.w || !plot.h) return [0, 0];
  return [plot.x + plot.w / 2, plot.y + plot.h / 2];
}

/**
 * The parcel a world point is in, by tile set and never by bounding box (D149). An L-shaped
 * field and the field sitting in its notch overlap as rectangles and not as tile sets, so
 * this is the difference between a tap landing on the field the player aimed at and on its
 * neighbour.
 */
export function hitParcel(parcels: IsoParcel[], x: number, y: number): IsoParcel | null {
  for (const p of parcels) if (inUnion(p.tiles, x, y)) return p;
  return null;
}

/** The tile a world point falls in, as a tile key. */
export const tileAt = (x: number, y: number): { x: number; y: number } => ({
  x: Math.floor(x),
  y: Math.floor(y),
});

/**
 * The tiles a ring round a tile key should follow: the whole parcel holding it, so an
 * L-shaped field is ringed as one field rather than as the box round it (D149), and the
 * bare tile itself where no parcel owns it or the parcel is gravel (D192).
 */
export function ringTiles(
  parcels: IsoParcel[],
  key: string,
  buildings: IsoBuildingAt[] = [],
): string[] {
  const at = parseTileKey(key);
  if (!at) return [];
  const p = hitParcel(parcels, at[0] + 0.5, at[1] + 0.5);
  // D192: the yard, the road and the tracks are drawn as one gravel patch, but each tile is
  // its own thing with its own panel, so the ring goes round the one tile tapped.
  if (p) return p.t !== 'yard' ? [...p.tiles] : [key];
  // D340: a chain building is not a parcel, so its tiles are its own footprint.
  const b = buildings.find(
    (q) => q.id && at[0] >= q.x && at[0] < q.x + q.w && at[1] >= q.y && at[1] < q.y + q.h,
  );
  return b ? rectTiles(b) : [key];
}

/** The tile keys a building's own footprint covers (D340). */
export function rectTiles(b: { x: number; y: number; w: number; h: number }): TileKey[] {
  const out: TileKey[] = [];
  for (let y = Math.floor(b.y); y < b.y + b.h; y++)
    for (let x = Math.floor(b.x); x < b.x + b.w; x++) out.push(`${x},${y}`);
  return out;
}

/* ---------- events ----------
   D174: pointer events are the single model for mouse, touch and pen, so the shape below
   is the part of a pointer event the map reads. Everything is optional because these
   handlers are also called from the tests with a bare object. */

interface TouchLike {
  readonly clientX: number;
  readonly clientY: number;
}
/** The element a pointer can be captured on. Structural, so no DOM is needed to test it. */
interface CaptureTarget {
  setPointerCapture?: (id: number) => void;
  releasePointerCapture?: (id: number) => void;
}
interface MapPointerEvent {
  readonly clientX?: number;
  readonly clientY?: number;
  readonly pointerId?: number;
  readonly target?: unknown;
  readonly currentTarget?: CaptureTarget | null;
}

/**
 * The attribute that marks something floating over the map as one of its controls rather
 * than as map. The zoom cluster carries it, and so should anything a caller hangs in
 * `children` that can be pressed: the pan and tap handlers sit on the wrapper those things
 * live in, so without this a press on a button also lands on the parcel under it.
 */
export const MAP_CONTROL_ATTR = 'data-map-control';

/** The element shape the control test needs. Kept structural so it is testable with no DOM. */
interface NodeLike {
  getAttribute?: (name: string) => string | null;
  parentElement?: NodeLike | null | undefined;
}

/**
 * Whether a press landed on one of the map's own controls. Walks up from the target, so a
 * press on the `+` glyph inside the zoom button counts as the button. The depth cap is a
 * guard against a cyclic chain rather than a real limit: the controls are two deep.
 */
export function isMapControl(target: unknown): boolean {
  let n = target as NodeLike | null | undefined;
  for (let i = 0; n && i < 32; i++) {
    if (n.getAttribute?.(MAP_CONTROL_ATTR) != null) return true;
    n = n.parentElement;
  }
  return false;
}

/**
 * Whether a press is the map's to read. A press that began or ended on a control is the
 * control's alone: tapping Zoom in must zoom and nothing else.
 */
export const isMapPress = (e: MapPointerEvent): boolean => !isMapControl(e.target);

export interface DragStart {
  x: number;
  y: number;
  vx: number;
  vy: number;
  moved: number;
}

/** What the map is in the middle of. Exactly one of the two is set, or neither. */
export interface GestureState {
  drag: DragStart | null;
  pinch: PinchStart | null;
}

export const NO_GESTURE: GestureState = { drag: null, pinch: null };

/**
 * The gesture the map is in, given the pointers that are down (D174).
 *
 * Pure, and the whole state machine: with pointer events there is one family of events
 * for mouse, touch and pen, so a pinch is two pointers rather than a second event path,
 * and this decides which of the two the map is doing every time a pointer arrives or
 * leaves. The rules that are easy to get wrong and are therefore tested directly:
 * a second pointer abandons the pan rather than adding to it, or the map jumps by
 * whatever the first finger had already moved; a pinch that drops back to one pointer
 * resumes a pan already past the tap slop, so the finger still down cannot select
 * anything when it lifts; and no pointers at all means no gesture, which is how a
 * cancelled touch recovers rather than leaving a drag half-open.
 *
 * `rect` is the map's frame. Without one there is nothing to hold a pinch's midpoint
 * still, so a pinch cannot start and the first pointer keeps panning.
 */
export function nextGesture(
  active: readonly TouchLike[],
  view: View,
  rect: { left: number; top: number } | null,
  prev: GestureState = NO_GESTURE,
): GestureState {
  const a = active[0];
  const b = active[1];
  if (!a) return NO_GESTURE;
  if (b && rect) {
    // Already pinching: a third finger landing must not restart the scale from here.
    if (prev.pinch) return { drag: null, pinch: prev.pinch };
    return {
      drag: null,
      pinch: {
        dist: Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY),
        mx: (a.clientX + b.clientX) / 2 - rect.left,
        my: (a.clientY + b.clientY) / 2 - rect.top,
        k: view.k,
        x: view.x,
        y: view.y,
      },
    };
  }
  if (!prev.pinch && prev.drag) return prev;
  return {
    pinch: null,
    drag: {
      x: a.clientX,
      y: a.clientY,
      vx: view.x,
      vy: view.y,
      moved: prev.pinch ? TAP_SLOP + 1 : 0,
    },
  };
}

/** A building standing on the map. Same shape the idle map hands in. */
export interface IsoBuildingAt {
  x: number;
  y: number;
  w: number;
  h: number;
  kind: BuildingKind;
  /** D196: what it is for. Tints the roof and names the sign; none keeps the slate. */
  use?: BuildingUse;
  /** D60: false flags it on the power overlay. */
  powered?: boolean;
  /** D268: a plot's building steps up from lean-to to timber to stone. */
  style?: BuildingStyle;
  /** D291: the building decoration on show. */
  look?: string;
  /** D340: the world list's row this building's tap finds (its producer's, once built, since
   *  the chain's own row is then gone). The house and the barn carry none: tapping them
   *  selects nothing. */
  id?: string;
  /** D343: the chain's own id, distinct from `id` above, for its own roof (`CHAIN_ROOF`). */
  chain?: string;
  /** D422, D423: on a theme whose home is a heart, the home: the heart on the whole yard. */
  heart?: Heart;
  /** D430: on the home, the world's age, for a country that grows with it (`worldAge`). */
  age?: { years: number; generations: number };
}

export interface IsoWorldMapProps {
  /** The world as parcels, from `isoParcelsOf`. */
  parcels?: IsoParcel[];
  /** The owned land, from `isoPlot`. The slab everything stands on. */
  plot?: Bounds | null;
  /** D301: the main world's own edge, for its hedge, apart from any open land beside it. */
  worldPlot?: WorldBounds | null;
  /** D301: each open piece of land beside the world, hedged round all four sides of its own. */
  lands?: Bounds[];
  buildings?: IsoBuildingAt[];
  /** Land outside the plot: the seeded trees (D267) and the neighbours' land (D329). */
  country?: Array<{
    x: number;
    y: number;
    w: number;
    h: number;
    t: string;
    k?: string;
    r?: number;
    s?: number;
  }>;
  stream?: Point[];
  lane?: number;
  hedges?: boolean;
  /** D305: off hides the tier sign (D268) on every plot. On by default. */
  showSigns?: boolean;
  /** D331: the hedge and gate decorations on show, by id, and where the world gate stands. */
  decor?: {
    hedge?: string;
    gate?: string;
    /** D420: the fence decoration, in a theme with no pens, for its boundary. */
    fence?: string;
    gateAt?: { x: number; y: number; px: number; py: number };
    /** D437: the open track (D287), drawn as the theme's lanes. */
    track?: {
      surface: string;
      edges: ReadonlyArray<{
        from: { x: number; y: number };
        to: { x: number; y: number };
        wet: boolean;
      }>;
    };
    /** D437: the gaps between neighbouring plots, paved in the theme's paving. */
    paving?: readonly string[];
    /** D440: how far winter's snow has come, 0 to 1. */
    snow?: number;
    /** D440: a filler on each lone tile the seed left. */
    fillers?: ReadonlyArray<{ key: string; x: number; y: number; variant: number }>;
  };
  season?: SeasonKey | string;
  time?: TimeKey | string;
  /** D144: the region weather, laid over the finished scene with the hour's cast. */
  weather?: Weather;
  /** The selected tile key, `g.sel`. The parcel holding it is outlined. */
  selected?: string | null;
  /** D60: which overlay the Key has on. The captions are the screen's own. */
  overlay?: IsoOverlay;
  /** Soil by tile key, from `soilMap`. Read by the soil overlay. */
  soil?: Record<string, number>;
  /** D116: the tiles the world's best tractor can reach, from `bestReach`. */
  reachEdge?: string[];
  /**
   * D54, D131 and D132: tile keys to draw as a ghost footprint, "these, if you confirm".
   * The same prop the flat map takes, and distinct from the selection and the guide ring.
   */
  highlight?: string[];
  /** D44: the tile the walkthrough is pointing at. The parcel holding it is ringed. */
  guide?: string | null;
  /** D87: bumped by "Show me", so the ring plays its stronger turn once. */
  guidePulse?: number;
  /** A tap. The tile is the one under the finger; `rowId` is the producer or chain it
   *  belongs to, if any (D340), for the caller to scroll the world list to. */
  onSelect?: (t: { x: number; y: number }, rowId: string | undefined) => void;
  /** D345: a tap landed on a crop waiting to be tapped (its own bobbing icon): the producer
   *  to collect, exactly as the row's own Tap does. Checked before `onSelect`. */
  onCollect?: (producer: string, at: { clientX: number; clientY: number }) => void;
  /**
   * D353: the render clock, ms, that a field a hand works runs its own cycle on (`atCycle`).
   * Only the clock moves it, never a change to the parcels, so a tap or a store change never
   * steps a field. Left out, or under reduced motion, every such field holds still.
   */
  clock?: number;
  /**
   * D168: a picture of a world rather than the screen you play on. No pan, no zoom, no tap
   * and no controls; the map fits its plot to the frame it is given and stays there. The
   * frame itself is the plot's own aspect unless the caller sets a height, so a still in a
   * card or a sheet takes exactly the height its diamond needs.
   */
  still?: boolean;
  /** What the map is a map of. "<World> map" in the theme's word unless the caller has a better name for it. */
  label?: string;
  style?: JSX.CSSProperties;
  /** D348: the world's theme, which picks the map kit that paints it. The world's by default. */
  theme?: string;
  /** Anything drawn over the map: the overlays, the placement ghost, the screen's own bits. */
  children?: JSX.Element | JSX.Element[] | null;
}

/** D259: a festival mark that has just gone down, drawn as its own element. */
export interface FreshMark {
  mark: string;
  /** The animation it plays: `map-mark-new`, `map-busy` or `map-burst`. */
  cls: string;
}

/** D259: what stands on a still map and how tall, for the fit: the theme's own (D348). */
export function standingOf(
  buildings: IsoBuildingAt[] = [],
  parcels: IsoParcel[] = [],
  kit: MapKit = mapKitFor(),
): Standing[] {
  return kit.flat.standing(buildings, parcels);
}

/**
 * A machine on the map (D164): where its sprite is put, the travel along its run, and the
 * sprite itself. The sprite is painted at the origin, so the position is one transform and
 * the travel is a second one on a group inside it; that inner group is the only thing that
 * animates, and it keeps animating while the sprite under it is repainted.
 */
export interface MachineNode {
  /**
   * The translate that puts the sprite on its lane. The machine is painted about the world
   * origin, which is the screen origin only at rest: once the world is turned about the
   * plot's centre, the world origin projects somewhere else entirely, and that offset is
   * taken out here or the machine stands off the world at three turns out of four.
   */
  tx: number;
  ty: number;
  /** Screen offset from there to the far end of its run. The near end is the negative. */
  dx: number;
  dy: number;
  /** Seconds for one there-and-back, and where in that cycle this machine starts. */
  dur: number;
  delay: number;
  html: string;
}

export function machineNode(at: MachineAt, P: Palette, kit: MapKit = mapKitFor()): MachineNode {
  const o: string[] = [];
  kit.flat.machine(at, P, o);
  const mid = project(at.x, at.y);
  const origin = project(0, 0);
  const end = project(at.run.to[0], at.run.to[1]);
  const dur = runSeconds(at.run.len);
  /* A deterministic phase, so two machines on one world are not in lockstep and the same
     farm looks the same on every load. Nothing here is rolled. */
  const phase = Math.abs(at.x * 7.3 + at.y * 3.1) % 1;
  return {
    tx: mid[0] - origin[0],
    ty: mid[1] - origin[1],
    dx: end[0] - mid[0],
    dy: end[1] - mid[1],
    dur,
    delay: -phase * dur,
    html: o.join(''),
  };
}

/** The run as CSS: the travel in custom properties, the pace as the animation's own. */
export const runStyle = (m: MachineNode): JSX.CSSProperties =>
  ({
    '--map-run-x': `${m.dx.toFixed(1)}px`,
    '--map-run-y': `${m.dy.toFixed(1)}px`,
    animationDuration: `${m.dur}s`,
    animationDelay: `${m.delay.toFixed(1)}s`,
  }) as unknown as JSX.CSSProperties;

/** D230: the labelled controls in the cluster share one width sized to the widest of them,
 * "Above" among them, rather than a guess of how long that word runs. Measured in the same
 * font and size the labelled buttons render in below. */
const CONTROL_LABEL_FONT = "500 11px 'Figtree', system-ui, -apple-system, 'Segoe UI', sans-serif";
const CONTROL_LABELS = [MAP_TURN_BUTTON, MAP_PLAN_BUTTON, 'Fit'];
/** Padding either side of a label inside its button. */
const CONTROL_PAD_X = 8;

/** The widest label's own width in `CONTROL_LABEL_FONT`. A canvas measures the real text; off
 * the browser (SSR, a test with no canvas) a width from the font size and the label's length
 * stands in, generous rather than tight, so the column is never narrower than the word needs. */
function widestLabelWidth(): number {
  const ctx =
    typeof document === 'undefined' ? null : document.createElement('canvas').getContext('2d');
  if (ctx) ctx.font = CONTROL_LABEL_FONT;
  const widthOf = (label: string): number =>
    ctx ? ctx.measureText(label).width : label.length * 11 * 0.62;
  return Math.max(...CONTROL_LABELS.map(widthOf));
}

/** D229 and D230: the zoom cluster's own width, wide enough for its widest label with room to
 * spare either side, so anything else over the map (the "Next up" chip) can read it rather
 * than guess a number that would drift from this one. Never narrower than the square icon
 * buttons (+ and -) need. */
export const ZOOM_CLUSTER_W = Math.ceil(Math.max(34, widestLabelWidth() + CONTROL_PAD_X * 2));

const zoomButton: JSX.CSSProperties = {
  width: ZOOM_CLUSTER_W,
  height: 34,
  border: '1px solid var(--line)',
  borderRadius: 'var(--radius-sm)',
  background: 'var(--surface)',
  color: 'var(--ink)',
  font: '500 16px/1 var(--font-ui)',
  cursor: 'pointer',
  display: 'grid',
  placeItems: 'center',
  whiteSpace: 'nowrap',
};

export function IsoWorldMap({
  parcels: given = [],
  plot = null,
  worldPlot = null,
  lands = [],
  buildings = [],
  country = [],
  stream = [],
  lane,
  hedges = true,
  showSigns = true,
  decor,
  season = 'summer',
  time = 'morning',
  weather,
  selected,
  overlay = 'none',
  soil,
  reachEdge,
  highlight,
  guide,
  guidePulse = 0,
  onSelect,
  onCollect,
  clock,
  still = false,
  label = `${cap(term('world'))} map`,
  style,
  theme,
  children,
}: IsoWorldMapProps) {
  /* D348: everything themed is painted by the theme's own map kit. */
  const kit = mapKitFor(theme);
  /* D353: each worked field as it looks at this point in its own cycle. */
  const [moving] = useState(() => !prefersReducedMotion());
  const parcels = useMemo(
    () => given.map((p) => atCycle(p, moving ? clock : undefined, kit.fieldCycle)),
    [given, moving, clock, kit],
  );
  const wrapRef = useRef<HTMLDivElement>(null);
  const [view, setView] = useState<View>({ x: 0, y: 0, k: 1 });
  /* D176: which of the four orientations the world is shown at. View state and nothing
     more — it lives on the device, the server never hears of it and the sim never learns
     there is such a thing. A still is a picture in a card and takes no controls, so it is
     always painted at rest (D168). */
  const [turn, setTurn] = useState<Turn>(0);
  const T: Turn = still ? 0 : turn;
  /* D178: whether the world is seen from directly above. The turn keeps working from above,
     so a player who has oriented the farm one way does not lose that by looking down at
     it. A still is the farm's portrait rather than a tool for reading it, and stays
     isometric whatever this device remembers. */
  const [plan, setPlan] = useState<boolean>(readPlanView);
  const V = still ? false : plan;
  const pivot = turnPivot(plot);
  /* Numbers, not strings: a zero here is a measurement that has not happened yet, and the
     fit must be able to tell that from a real zero. */
  const [size, setSize] = useState<{ w: number; h: number }>({ w: 0, h: 0 });
  /* The map's colours live in the --map-* token layer, so they are re-read whenever the
     theme changes. Resolving against this element picks up a data-theme set on any
     ancestor, not only on the document, which is where this project puts it. */
  const [tokens, setTokens] = useState<MapTokens | null>(null);
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const read = () => setTokens(resolveMapTokens(el));
    read();
    if (typeof MutationObserver === 'undefined') return;
    const mo = new MutationObserver(read);
    /* The root only, not its subtree: the theme is set on document.documentElement and
       nowhere else, while a subtree watch re-reads all the tokens of every mounted map
       whenever any element anywhere changes a class. With the stills of D168 there are
       several maps mounted at once, and each needless re-read is another chance to catch
       a moment when the properties are unreachable (D173). */
    mo.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme', 'class'],
    });
    return () => mo.disconnect();
  }, []);
  const P = useMemo(
    // D440: winter's snow over the whole map, as far as it has come.
    () => snowed(kit.palette(season, time, tokens, weather), decor?.snow ?? 0),
    [kit, season, time, tokens, weather, decor?.snow],
  );

  const drag = useRef<DragStart | null>(null);
  const pinch = useRef<PinchStart | null>(null);

  /* Measure after layout, not during mount: the host sizes this element with a percentage
     that has not resolved yet, and a zero reading here would scale the whole map to
     nothing. The rAF retry catches that first frame; the observer catches the rest. */
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize((s) =>
        Math.abs(s.w - r.width) > 0.5 || Math.abs(s.h - r.height) > 0.5
          ? { w: r.width, h: r.height }
          : s,
      );
    };
    measure();
    const raf = typeof requestAnimationFrame === 'undefined' ? 0 : requestAnimationFrame(measure);
    if (typeof ResizeObserver === 'undefined') return () => cancelAnimationFrame(raf);
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
    };
  }, []);

  /* D174: the drag is stopped from also scrolling the column the map sits in, explicitly
     rather than by trusting `touch-action: none` alone. Two reasons not to trust it. The
     thing under the finger is an SVG shape inside the wrapper, and `touch-action` is
     defined on elements that generate a CSS box, which SVG children do not; and Preact
     binds its handlers passively enough that `preventDefault` from a JSX `onTouchMove` is
     not dependable. So the listener is added by hand and non-passive. Safari's own
     `gesturestart` goes with it, or a pinch on the map zooms the page under it.

     A still binds nothing at all (D168), so it takes none of this either. */
  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el || still) return;
    const holdTheColumn = (e: Event) => {
      if (drag.current || pinch.current) e.preventDefault();
    };
    const noPageZoom = (e: Event) => e.preventDefault();
    el.addEventListener('touchmove', holdTheColumn, { passive: false });
    el.addEventListener('gesturestart', noPageZoom);
    return () => {
      el.removeEventListener('touchmove', holdTheColumn);
      el.removeEventListener('gesturestart', noPageZoom);
    };
  }, [still]);

  /* The turn Fit measures at is read through a ref rather than closed over, so turning the
     farm does not change this callback and retrigger the effect below. Turning about the
     plot's own centre leaves the farm framed where it was; the zoom and the pan the player
     set are theirs to keep. Pressing Fit at any orientation frames that orientation. */
  const turnRef = useRef<{ t: Turn; p: Point; v: boolean }>({ t: T, p: pivot, v: V });
  turnRef.current = { t: T, p: pivot, v: V };
  /* D259: a still frames everything drawn on it, the tops of its buildings and marks
     included, so nothing at the back is clipped at any stage of the farm. */
  const standing = useMemo(
    () => (still || kit.tall ? kit.flat.standing(buildings, parcels) : null),
    [kit, still, buildings, parcels],
  );
  const standKey = standing
    ? standing.map((s) => `${s.x},${s.y},${s.w},${s.h},${s.z}`).join(';')
    : '';
  const fit = useCallback(() => {
    const { t, p, v } = turnRef.current;
    const next = withPlan(v, () =>
      withTurn(t, p[0], p[1], () =>
        standing ? fitDrawn(plot, standing, size.w, size.h) : fitView(plot, size.w, size.h),
      ),
    );
    if (next) setView(next);
  }, [plot?.x, plot?.y, plot?.w, plot?.h, size.w, size.h, standKey]);
  /* Fit on the first measurement that is not zero, and again when the frame or the plot
     changes materially. `fitView` returning null is what keeps a zero frame out. */
  /* D178 adds the projection to that list. A turn holds the pivot still and leaves the
     framing alone, but the two projections do not agree on how big a tile is, so switching
     between them without fitting would leave the farm at some arbitrary size and offset.
     Fit is what frames the farm from above, and it runs the moment the view changes. */
  useLayoutEffect(() => {
    fit();
  }, [fit, V]);

  const frame = (): DOMRect | null => wrapRef.current?.getBoundingClientRect() ?? null;

  const worldAt = (clientX: number, clientY: number): Point | null => {
    const r = frame();
    if (!r) return null;
    /* The inverse at the same turn as the paint, which is the whole of what makes a tap
       land on the field the finger is over rather than on its neighbour (D176). */
    return withPlan(V, () =>
      withTurn(T, pivot[0], pivot[1], () =>
        unproject((clientX - r.left - view.x) / view.k, (clientY - r.top - view.y) / view.k),
      ),
    );
  };

  const zoom = (factor: number, cx?: number, cy?: number) =>
    setView((v) => {
      const r = frame();
      if (!r) return v;
      const px = cx === undefined ? r.width / 2 : cx - r.left;
      const py = cy === undefined ? r.height / 2 : cy - r.top;
      return zoomAbout(v, factor, px, py);
    });

  /* D174. Every pointer that is down on the map, by id. One model covers mouse, touch and
     pen; a pinch is simply the moment there are two of them, which is why there is no
     touch path left beside this one. */
  const pointers = useRef(new Map<number, TouchLike>());

  /** Recompute the gesture from the pointers that are down. Called when that set changes. */
  const settle = () => {
    const g = nextGesture([...pointers.current.values()], view, frame(), {
      drag: drag.current,
      pinch: pinch.current,
    });
    drag.current = g.drag;
    pinch.current = g.pinch;
  };

  const onDown = (e: MapPointerEvent) => {
    /* A press on the zoom cluster is the button's, not the map's. Dropping the pointers
       as well as returning means no pan can carry over into it either. */
    if (!isMapPress(e)) {
      pointers.current.clear();
      drag.current = null;
      pinch.current = null;
      return;
    }
    if (e.clientX === undefined || e.clientY === undefined) return;
    const id = e.pointerId ?? 0;
    pointers.current.set(id, { clientX: e.clientX, clientY: e.clientY });
    /* Pointer capture: every later move and the release for this pointer belong to the
       map even once the finger has crossed its edge. On a map that fills a phone's width
       a drag that dies at the edge is indistinguishable from a map that does not pan. */
    try {
      e.currentTarget?.setPointerCapture?.(id);
    } catch {
      /* The pointer was already gone. There is nothing to capture and nothing to do. */
    }
    settle();
  };

  const onMove = (e: MapPointerEvent) => {
    const id = e.pointerId ?? 0;
    if (!pointers.current.has(id) || e.clientX === undefined || e.clientY === undefined) return;
    pointers.current.set(id, { clientX: e.clientX, clientY: e.clientY });
    const start = pinch.current;
    if (start) {
      const [a, b] = [...pointers.current.values()];
      if (a && b)
        setView(pinchView(start, Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)));
      return;
    }
    const d = drag.current;
    if (!d) return;
    const dx = e.clientX - d.x,
      dy = e.clientY - d.y;
    d.moved = Math.max(d.moved, Math.abs(dx) + Math.abs(dy));
    setView((v) => ({ ...v, x: d.vx + dx, y: d.vy + dy }));
  };

  /**
   * D340: what a tap at this point selects: the tile under it, and the producer or chain
   * row it belongs to, if any. Shared by the full pan-and-zoom gesture below and by a
   * still's own plain click, so the two can never disagree about what was tapped.
   */
  const selectAt = (clientX: number, clientY: number) => {
    // D345: a crop's own bobbing "ready" icon sits above the ground, so it is tried first,
    // by its own screen position rather than the world point under the finger.
    const r = frame();
    if (r && onCollect) {
      for (const p of parcels) {
        if (!p.ready || !p.id || p.due === false) continue;
        const b = unionBounds(p.tiles);
        const [sx, sy] = withPlan(V, () =>
          withTurn(T, pivot[0], pivot[1], () => project(b.x + b.w / 2, b.y + b.h / 2)),
        );
        const px = r.left + sx * view.k + view.x,
          py = r.top + sy * view.k + view.y - READY_LIFT * view.k;
        if (Math.hypot(clientX - px, clientY - py) <= READY_TAP_R) {
          onCollect(p.id, { clientX: px, clientY: py });
          return;
        }
      }
    }
    if (!onSelect) return;
    const at = worldAt(clientX, clientY);
    if (!at) return;
    // D149: the parcel is the one whose tiles hold this point, not the one whose box does.
    const p = hitParcel(parcels, at[0], at[1]);
    // D340: a chain building is not a parcel, so it is tried by its own footprint box.
    const b = p
      ? undefined
      : buildings.find(
          (q) => q.id && at[0] >= q.x && at[0] < q.x + q.w && at[1] >= q.y && at[1] < q.y + q.h,
        );
    onSelect(tileAt(at[0], at[1]), p?.id ?? b?.id);
  };

  /** A still click: a still takes no pan or zoom, but a producer, pen or chain building on
   *  it still answers a tap (D340), without the full pan-and-zoom gesture below. */
  const onStillClick = (e: MapPointerEvent) => {
    if (e.clientX === undefined || e.clientY === undefined) return;
    selectAt(e.clientX, e.clientY);
  };

  /** A pointer leaves. `tap` is false when the gesture was cancelled rather than lifted. */
  const release = (e: MapPointerEvent, tap: boolean) => {
    const id = e.pointerId ?? 0;
    const held = pointers.current.delete(id);
    try {
      e.currentTarget?.releasePointerCapture?.(id);
    } catch {
      /* Already released, which is what pointercancel does for us. */
    }
    const pinching = pinch.current !== null;
    const d = drag.current;
    settle();
    /* Lifting one finger of two resumes a pan rather than firing a tap; `nextGesture`
       starts that pan past the slop so the finger still down selects nothing. */
    if (!tap || pinching || !held) return;
    /* A press that ended over a control is the control's too: a finger that started on the
       ground and lifted on the zoom button must not select what is under the button. */
    if (!d || d.moved > TAP_SLOP || !isMapPress(e)) return;
    if (e.clientX === undefined || e.clientY === undefined) return;
    selectAt(e.clientX, e.clientY);
  };

  const onUp = (e: MapPointerEvent) => release(e, true);
  /* D174: iOS cancels touches when the system takes the gesture over — an edge swipe, a
     call arriving — and a drag left half-open never recovers. The pointer is dropped and
     the gesture settles back to nothing, with no tap fired for a gesture nobody finished. */
  const onCancel = (e: MapPointerEvent) => release(e, false);

  const scene = useMemo(
    () =>
      withPlan(V, () =>
        withTurn(T, pivot[0], pivot[1], () => {
          const o: string[] = [];
          /* The country, hazed back so the plot carries the eye. Sized off the plot rather than
       off a fixed square, because a farm grows to 40 tiles a side. */
          const far = plot ?? { x: 0, y: 0, w: 20, h: 20 };
          /* The country, its edge water and what stands on it, the theme's own (D348). */
          kit.flat.scenery(
            {
              far,
              country,
              stream,
              lane: lane ?? null,
              season: String(season),
              lands,
              parcels,
              age: worldAge(buildings),
            },
            P,
            o,
          );
          /* The slab: the ground the world stands on, with its thickness showing. D301: only
             fenced land gets a slab, the farm's own and each open piece beside it, so the
             gap between them (the river's own ground) is the plain country grass, not
             another raised block reading as a hole cut out of the world. */
          const paintSlab = (b: Bounds) => {
            if (isPlan()) {
              /* D178: from above the slab has no skirt, because there is no side to see. The
               edge is then the line where the farm's soil stops and the country begins, so
               it is drawn as that line rather than left to a change of colour. */
              const ring = rectPts(b.x, b.y, b.w, b.h);
              o.push(
                `<path d="${polyPath(ring)}" fill="${P.soil}" stroke="${shade(P.soil, 0.42)}" stroke-width="2.5" stroke-linejoin="round"/>`,
              );
              return;
            }
            const ring = rectPts(b.x, b.y, b.w, b.h) as [Point, Point, Point, Point];
            /* The two sides of the slab that show are the two meeting at the corner nearest
             the eye, and which corner that is changes with the turn (D176). At rest it is
             the same pair this has always drawn. */
            const fc = frontCorner(b.x, b.y, b.w, b.h);
            const L = ring[(fc + 1) % 4] as Point,
              F = ring[fc] as Point,
              R = ring[(fc + 3) % 4] as Point;
            const drop = (q: Point, by = SLAB): Point => [q[0], q[1] + by];
            o.push(
              `<path d="${polyPath([L, F, drop(F, SLAB + 7), drop(L, SLAB + 7)])}" fill="rgba(42,36,24,.16)"/>`,
            );
            o.push(
              `<path d="${polyPath([L, F, drop(F), drop(L)])}" fill="${shade(P.soil, 0.42)}"/>`,
            );
            o.push(
              `<path d="${polyPath([F, R, drop(R), drop(F)])}" fill="${shade(P.soil, 0.26)}"/>`,
            );
            o.push(`<path d="${polyPath(ring)}" fill="${P.soil}"/>`);
          };
          // D344: a kind's outline is a few rectangles of slab, from the back.
          const outline = (worldPlot ?? plot) ? worldOutline((worldPlot ?? plot)!) : null;
          for (const s of outline?.slabs ?? []) paintSlab(s);
          for (const l of lands) paintSlab(l);
          for (const p of parcels) kit.flat.ground(p, P, o);
          /* A field with a job shows the ground already worked, and the machine on the line
       between turned and standing. The machine itself sorts with the other props. */
          const working: MachineAt[] = [];
          for (const p of parcels) working.push(...kit.flat.fieldJob(p, P, o));
          /* D161: a bank between every two parcels, so one field is told from the next whatever
       is growing on either. It goes down with the land, after the worked ground of a
       running job and before anything that stands on the land, so a tree or a barn in
       front of a bank still covers it. */
          /* D161, D301, D331, D344: the banks between parcels, the world's own edge and its gate.
             They go down with the land, after the worked ground of a running job and before
             anything that stands on the land, so a tree or a barn in front still covers them. */
          // D422: each plot's own edge, its pad and rim, under the boundaries.
          kit.flat.plotEdges?.(parcels, P, o);
          // D437: the paving between neighbouring plots, and the lanes over it.
          if (decor?.paving) paintPaving(decor.paving, P, o);
          if (decor?.track) paintLanes(decor.track, kit.id, o);
          kit.flat.edges({ parcels, outline, lands, hedges, ...(decor ? { decor } : {}) }, P, o);
          const stand: Array<
            | Prop
            | { k: 'machine'; x: number; y: number; at: MachineAt }
            | (FillerProp & { w: number; h: number })
          > = [];
          for (const p of parcels) stand.push(...kit.flat.props(p, showSigns));
          // D440: a filler on each lone tile, sorted with everything else that stands.
          for (const f of decor?.fillers ?? [])
            stand.push({ k: 'filler', x: f.x, y: f.y, w: 1, h: 1, variant: f.variant });
          for (const b of buildings) stand.push({ k: 'build', ...b });
          for (const w of working) stand.push({ k: 'machine', x: w.x, y: w.y, at: w });
          /* Back to front at this turn. `x + y` is what that comes to at rest and is wrong at
       the other three, which is how a barn ends up drawn through the field in front of
       it. */
          /* D273: by footprint, not by one point each: a coop's anchor is its back corner,
             and a hen just behind it sorted after that point and stood on its roof. */
          const sorted = sortStanding(stand);
          /* D164: a machine travels, so it cannot live in the one innerHTML string with
       everything else — that string is rebuilt on every clock tick and the travel would
       start again from the top once a second. It is cut here instead: one string up to
       each machine, the machine as an element of its own, and the rest after it. The
       back-to-front order is the sort's, exactly as it was, so a barn in front of a
       working field still covers the tractor on it. */
          const parts: string[] = [];
          const machines: Array<MachineNode | FreshMark> = [];
          for (const s of sorted) {
            if (s.k === 'machine') {
              parts.push(o.splice(0).join(''));
              machines.push(machineNode(s.at, P, kit));
            } else if ((s.k === 'mark' && s.fresh) || s.k === 'busy' || s.k === 'burst') {
              /* D259, D268, D269: a mark just put down, a milestone's moment and a busy
                 plot's loop are each their own element, like a machine, so the animation
                 plays on and is not restarted by the next tick's repaint. */
              parts.push(o.splice(0).join(''));
              const q: string[] = [];
              kit.flat.prop(s, P, q);
              const cls =
                s.k === 'mark' ? 'map-mark-new' : s.k === 'busy' ? 'map-busy' : 'map-burst';
              machines.push({ mark: q.join(''), cls });
            } else if (s.k === 'filler') paintFiller(kit.id, s, P, o);
            else kit.flat.prop(s, P, o);
          }
          kit.flat.weather(far, P, o);
          /* D150: the overlay washes land here, with the ground, so the hour's cast and the
       weather light them along with everything else. The marks are painted over the cast
       below, where nothing can swallow them. */
          paintOverlayWash(
            {
              overlay,
              parcels,
              buildings,
              ...(soil ? { soil } : {}),
              ...(reachEdge ? { reach: reachEdge } : {}),
            },
            P,
            o,
          );
          parts.push(o.splice(0).join(''));
          /* The ambient motion D164 asks for, and no more of it: smoke over the world buildings
       and light moving on the water. Both are elements rather than markup, for the same
       reason the machines are. */
          const smoke = kit.flat.smoke(buildings, parcels);
          const ripples = kit.flat.ripples(parcels);
          return { parts, machines, smoke, ripples };
        }),
      ),
    [
      parcels,
      country,
      stream,
      lane,
      plot,
      worldPlot,
      lands,
      buildings,
      hedges,
      showSigns,
      decor?.hedge,
      decor?.gate,
      decor?.fence,
      decor?.gateAt,
      decor?.track,
      decor?.paving,
      overlay,
      soil,
      reachEdge,
      P,
      T,
      V,
      pivot[0],
      pivot[1],
      kit,
    ],
  );

  /* D150: everything that must stay readable whatever the hour. Its own SVG over the cast
     divs, with the same transform, so the reach line, the power flag, the ghost, the
     selection ring and the guide ring are never washed out. */
  const marks = useMemo(
    () =>
      withPlan(V, () =>
        withTurn(T, pivot[0], pivot[1], () => {
          const o: string[] = [];
          paintOverlayMarks(
            { overlay, parcels, buildings, ...(reachEdge ? { reach: reachEdge } : {}) },
            P,
            o,
          );
          if (highlight?.length) paintGhost(highlight, o);
          if (selected) paintSelection(ringTiles(parcels, selected, buildings), o);
          if (guide) paintGuide(ringTiles(parcels, guide), guidePulse, o);
          return o.join('');
        }),
      ),
    [
      parcels,
      buildings,
      overlay,
      reachEdge,
      highlight,
      selected,
      guide,
      guidePulse,
      P,
      T,
      V,
      pivot[0],
      pivot[1],
    ],
  );

  /* D168: a still takes none of the pan-and-zoom plumbing below. The handlers are simply
     not bound, so a press on it is a press on whatever the still is sitting in — a card or
     a sheet that scrolls — rather than something the map swallows and drops. D340: a still
     with `onSelect` is the exception, since the player can choose it as the farm's own map
     rather than a mere picture; it answers a tap, but takes no pan, no zoom and no controls. */
  const hands = still
    ? onSelect
      ? { onClick: onStillClick }
      : {}
    : {
        onPointerDown: onDown,
        onPointerMove: onMove,
        onPointerUp: onUp,
        onPointerCancel: onCancel,
        onWheel: (e: { deltaY: number; clientX: number; clientY: number }) =>
          zoom(e.deltaY < 0 ? WHEEL_IN : WHEEL_OUT, e.clientX, e.clientY),
      };

  return (
    <div
      ref={wrapRef}
      // The element the gesture is bound to, so the WebKit pan run of D174 can aim at it
      // rather than guess at a selector. A still binds nothing, so it is not one.
      data-testid={still ? undefined : 'map-surface'}
      {...hands}
      style={{
        position: 'relative',
        overflow: 'hidden',
        background: P.sky,
        userSelect: 'none',
        /* WebKit only dropped the prefix on `user-select` in Safari 17, so on an iPhone a
           release or two behind, the unprefixed line above is ignored and a drag across
           the map starts a selection instead. */
        WebkitUserSelect: 'none',
        ...(still
          ? /* The height follows the diamond, so a still in a flow column is as tall as
               its farm needs and no taller. A caller that sets a height wins. */
            { width: '100%', aspectRatio: String(stillAspect(plot)) }
          : {
              height: '100%',
              cursor: 'grab',
              touchAction: 'none',
              // No long-press callout over a map that is being dragged, and no rubber-band
              // of the column behind it when the pan reaches the edge of the world.
              WebkitTouchCallout: 'none',
              overscrollBehavior: 'none',
            }),
        ...style,
      }}
    >
      <svg
        width="100%"
        height="100%"
        style={{ display: 'block' }}
        role="img"
        aria-label={label}
        data-signs={view.k >= SIGN_ZOOM ? '' : undefined}
      >
        <g transform={`translate(${view.x},${view.y}) scale(${view.k})`}>
          {scene.parts.map((html, i) => {
            const m = scene.machines[i];
            return (
              <g key={`layer${i}`}>
                <g dangerouslySetInnerHTML={{ __html: html }} />
                {m && 'mark' in m ? (
                  <g class={m.cls} dangerouslySetInnerHTML={{ __html: m.mark }} />
                ) : m ? (
                  <g transform={`translate(${m.tx.toFixed(1)},${m.ty.toFixed(1)})`}>
                    {/* The travelling group. Its sprite is repainted on every tick; this
                        element is not, so the run carries on across a repaint. */}
                    <g
                      class="map-machine"
                      style={runStyle(m)}
                      dangerouslySetInnerHTML={{ __html: m.html }}
                    />
                  </g>
                ) : null}
              </g>
            );
          })}
          {scene.smoke.map((s, i) => (
            <circle
              key={`smoke${i}`}
              class="map-smoke"
              cx={s.cx.toFixed(1)}
              cy={s.cy.toFixed(1)}
              r={s.r.toFixed(1)}
              fill={SMOKE.colour}
              opacity="0"
              style={{ animationDelay: `${s.delay}s` }}
            />
          ))}
          {scene.ripples.map((r, i) => (
            <line
              key={`ripple${i}`}
              class="map-ripple"
              x1={r.x1.toFixed(1)}
              y1={r.y1.toFixed(1)}
              x2={r.x2.toFixed(1)}
              y2={r.y2.toFixed(1)}
              stroke="#ffffff"
              strokeWidth="1.6"
              strokeLinecap="round"
              opacity="0"
              style={{ animationDelay: `${r.delay}s` }}
            />
          ))}
        </g>
      </svg>
      {/* The hour's cast and the region weather, laid over the finished scene (D144). */}
      {P.casts.map((c, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            inset: 0,
            pointerEvents: 'none',
            background: c.fill,
            opacity: c.opacity,
            mixBlendMode: 'multiply',
          }}
        />
      ))}
      <svg
        width="100%"
        height="100%"
        aria-hidden="true"
        style={{
          position: 'absolute',
          inset: 0,
          display: 'block',
          pointerEvents: 'none',
        }}
      >
        <g
          transform={`translate(${view.x},${view.y}) scale(${view.k})`}
          dangerouslySetInnerHTML={{ __html: marks }}
        />
      </svg>
      {children}
      {/* D233: north, fixed to the screen rather than scaled with the map, so it reads the
          same at every zoom. Not a control: taps go through it to the farm beneath. */}
      {still ? null : <Compass deg={northOnScreen(T, V)} P={P} />}
      {/* The zoom cluster is the map's own control, not map: marked so `isMapPress` can
          tell, or a tap on Zoom in also opens the panel of whatever sits under it. A still
          has nothing to zoom, so it carries no cluster (D168). */}
      {still ? null : (
        <div
          {...{ [MAP_CONTROL_ATTR]: '' }}
          style={{
            position: 'absolute',
            right: OVER_MAP,
            bottom: OVER_MAP,
            display: 'grid',
            gap: 4,
          }}
        >
          <button
            type="button"
            aria-label="Zoom in"
            style={zoomButton}
            onClick={() => zoom(BUTTON_IN)}
          >
            +
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            style={zoomButton}
            onClick={() => zoom(BUTTON_OUT)}
          >
            &minus;
          </button>
          <button
            type="button"
            aria-label={`Fit ${term('world')}`}
            style={{ ...zoomButton, font: '500 11px/1 var(--font-ui)' }}
            onClick={fit}
          >
            Fit
          </button>
          {/* D176: one button that steps round the four orientations, not two. Four steps
              return to where it started, so a way back is already here. */}
          <button
            type="button"
            aria-label={mapTurnLabel()}
            style={{ ...zoomButton, font: '500 11px/1 var(--font-ui)' }}
            onClick={() => setTurn(stepTurn)}
          >
            {MAP_TURN_BUTTON}
          </button>
          {/* D178: the second projection is a toggle here, beside Turn, not a third state
              of the turn control and not a third option beside Map and List. It is shown
              pressed while the farm is seen from above, which is how the player can tell
              at a glance which of the two they are looking at. */}
          <button
            type="button"
            aria-label={mapPlanLabel()}
            aria-pressed={plan}
            style={{
              ...zoomButton,
              font: '500 11px/1 var(--font-ui)',
              ...(plan
                ? {
                    background: 'var(--accent)',
                    color: 'var(--accent-ink)',
                    borderColor: 'var(--accent)',
                  }
                : {}),
            }}
            onClick={() =>
              setPlan((p) => {
                writePlanView(!p);
                return !p;
              })
            }
          >
            {MAP_PLAN_BUTTON}
          </button>
        </div>
      )}
    </div>
  );
}

/** D233: the compass's size on screen, in CSS pixels. */
export const COMPASS_SIZE = 40;

/**
 * D233. A small rose in the map's own drawing language (D168): a disc of wall colour with a
 * base shadow, and a needle with a lit face and a shaded face, roof red to the north. The
 * needle turns to the angle `northOnScreen` gives; the N stays upright so it can be read.
 */
function Compass({ deg, P }: { deg: number; P: Palette }) {
  const c = COMPASS_SIZE / 2;
  const rad = (deg * Math.PI) / 180;
  const nx = c + Math.sin(rad) * 12.5,
    ny = c - Math.cos(rad) * 12.5;
  return (
    <svg
      data-testid="map-compass"
      role="img"
      aria-label={compassLabel(deg)}
      width={COMPASS_SIZE}
      height={COMPASS_SIZE}
      viewBox={`0 0 ${COMPASS_SIZE} ${COMPASS_SIZE}`}
      style={{ position: 'absolute', ...COMPASS_AT, pointerEvents: 'none', zIndex: 1 }}
    >
      <circle cx={c} cy={c + 1.5} r={c - 1.5} fill="#000000" opacity="0.18" />
      <circle cx={c} cy={c} r={c - 1.5} fill={P.wall} stroke={shade(P.wall, 0.35)} />
      <g transform={`rotate(${deg.toFixed(1)} ${c} ${c})`}>
        <path d={`M${c} ${c - 7} L${c - 3.5} ${c} L${c} ${c} Z`} fill={P.roof} />
        <path d={`M${c} ${c - 7} L${c + 3.5} ${c} L${c} ${c} Z`} fill={shade(P.roof, 0.3)} />
        <path d={`M${c} ${c + 7} L${c - 3.5} ${c} L${c} ${c} Z`} fill={P.stone} />
        <path d={`M${c} ${c + 7} L${c + 3.5} ${c} L${c} ${c} Z`} fill={shade(P.stone, 0.3)} />
      </g>
      <text
        x={nx.toFixed(1)}
        y={ny.toFixed(1)}
        text-anchor="middle"
        dominant-baseline="central"
        fill={shade(P.roof, 0.2)}
        style={{ font: '700 10px/1 var(--font-ui)' }}
      >
        {COMPASS_NORTH}
      </text>
    </svg>
  );
}

/** The job names the map has a rig for. Exported so a caller can tell before it draws. */
export const drawableJob = (name: string | null | undefined): boolean => !!name && !!JOB_RIG[name];
